import { useEffect, useRef, useState } from "react";
import { ImagePlus, Send, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useLanguage } from "@/i18n/LanguageContext";

const CATEGORIES = [
  { value: "soirees", label: "Soirée" },
  { value: "concerts", label: "Concert" },
  { value: "karaoke", label: "Karaoké" },
  { value: "humour", label: "Humour" },
  { value: "activites", label: "Activité" },
];

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[#2563EB] focus:ring-2 focus:ring-blue-100";

const EMPTY = {
  title: "", category_key: "soirees", description: "", image: "",
  date: "", time_start: "", time_end: "", venue: "", address: "", city: "Québec",
  price: "", is_free: true, ticket_url: "", contact: "",
};

// Modale de soumission d'événement par un membre (sans établissement) — création ou modification.
// Image obligatoire. Toute soumission passe en statut « En attente » jusqu'à validation admin.
export const PublicEventModal = ({ open, onClose, event, onSaved }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileInput = useRef(null);
  const editing = Boolean(event);
  const todayIso = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    if (!open) return;
    setError("");
    if (event) {
      const city = event.city || event.neighborhood || "Québec";
      let addr = event.address || "";
      if (addr.endsWith(`, ${city}`)) addr = addr.slice(0, -(city.length + 2));
      if (addr === event.venue) addr = "";
      setForm({
        title: event.title || "",
        category_key: event.category_key || "soirees",
        description: event.description || "",
        image: event.image || "",
        date: event.date || "",
        time_start: event.time_label || "",
        time_end: event.end_time || "",
        venue: event.venue || "",
        address: addr,
        city,
        price: event.price > 0 ? String(event.price) : "",
        is_free: !(event.price > 0),
        ticket_url: event.ticket_url || "",
        contact: event.contact || "",
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, event]);

  if (!open) return null;

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/pro/uploads", fd);
      setForm((f) => ({ ...f, image: data.url }));
      setError("");
    } catch (err) {
      toast.error(t(formatApiError(err.response?.data?.detail)));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.image) {
      setError(t("Ajoute une image pour illustrer ta fiche événement."));
      return;
    }
    if (!form.is_free && !(Number(form.price) > 0)) {
      setError(t("Indique le prix par personne ou coche Gratuit."));
      return;
    }
    setBusy(true);
    const payload = {
      title: form.title,
      category_key: form.category_key,
      description: form.description,
      image: form.image,
      date: form.date,
      time_start: form.time_start,
      time_end: form.time_end,
      venue: form.venue,
      address: form.address,
      city: form.city,
      price: form.is_free ? 0 : Number(form.price) || 0,
      ticket_url: form.ticket_url,
      contact: form.contact,
    };
    try {
      if (editing) {
        await api.put(`/events/public/${event.slug}`, payload);
        toast.success(t("Modifications enregistrées — ton événement repasse en validation."));
      } else {
        await api.post("/events/public", payload);
        toast.success(t("Événement soumis ! Il sera publié après validation par l'équipe GoSpot."));
      }
      onClose();
      onSaved?.();
    } catch (err) {
      setError(t(formatApiError(err.response?.data?.detail)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/50 p-3 sm:p-4" data-testid="publicevent-modal">
      <div className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
        {/* EN-TÊTE FIXE */}
        <div className="shrink-0 border-b border-slate-100 px-6 pb-4 pt-6 sm:px-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-xl font-extrabold tracking-tight text-slate-900">
                {editing ? t("Modifier l'événement") : t("Publier un événement")}
              </h3>
              <p className="mt-1 text-xs leading-relaxed text-slate-500">
                {t("Partage ton événement avec la communauté GoSpot — aucun établissement requis.")}
              </p>
            </div>
            <button
              type="button"
              data-testid="pe-close"
              onClick={onClose}
              aria-label={t("Fermer")}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition hover:bg-slate-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="mt-3 rounded-2xl bg-[#EFF6FF] px-4 py-2.5 text-xs font-medium text-[#2563EB]" data-testid="pe-moderation-note">
            {t("Ton événement sera vérifié par l'équipe GoSpot avant sa publication.")}
          </p>
        </div>

        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
          {/* CONTENU SCROLLABLE */}
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5 sm:px-8" style={{ WebkitOverflowScrolling: "touch" }} data-testid="pe-scroll-area">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-slate-700">{t("Titre de l’événement")}</label>
              <input data-testid="pe-title" required value={form.title} onChange={set("title")} placeholder={t("Ex. : Soirée jeux de société entre amis")} className={inputClass} />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-semibold text-slate-700">{t("Catégorie")}</label>
              <select data-testid="pe-category" value={form.category_key} onChange={set("category_key")} className={inputClass}>
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{t(c.label)}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-semibold text-slate-700">{t("Description")}</label>
              <textarea data-testid="pe-description" required rows={3} value={form.description} onChange={set("description")} placeholder={t("Décris l'ambiance, le programme, ce qui rend ton événement unique…")} className={inputClass} />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                {t("Image")} <span className="text-red-500">*</span>
              </label>
              {form.image ? (
                <div className="relative">
                  <img src={form.image} alt="" data-testid="pe-image-preview" className="h-36 w-full rounded-2xl object-cover" />
                  <button
                    type="button"
                    data-testid="pe-image-remove"
                    onClick={() => setForm((f) => ({ ...f, image: "" }))}
                    aria-label={t("Retirer l'image")}
                    className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-slate-900/60 text-white transition hover:bg-slate-900/80"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  data-testid="pe-image-upload"
                  onClick={() => fileInput.current?.click()}
                  disabled={uploading}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm font-semibold text-slate-500 transition hover:border-blue-300 hover:text-[#2563EB] disabled:opacity-60"
                >
                  <ImagePlus className="h-4 w-4" />
                  {uploading ? t("Téléversement…") : t("Choisir une image")}
                </button>
              )}
              <input ref={fileInput} type="file" accept="image/*" data-testid="pe-image-input" className="hidden" onChange={uploadImage} />
              <p className="mt-1.5 text-[11px] text-slate-400">{t("Format paysage recommandé (JPG ou PNG, max 10 Mo).")}</p>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">{t("Date")}</label>
                <input data-testid="pe-date" type="date" required min={todayIso} value={form.date} onChange={set("date")} className={inputClass} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">{t("Heure de début")}</label>
                <input data-testid="pe-time-start" required value={form.time_start} onChange={set("time_start")} placeholder="18h30" className={inputClass} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">{t("Heure de fin")}</label>
                <input data-testid="pe-time-end" value={form.time_end} onChange={set("time_end")} placeholder="23h00" className={inputClass} />
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-semibold text-slate-700">{t("Nom du lieu")}</label>
              <input data-testid="pe-venue" required value={form.venue} onChange={set("venue")} placeholder={t("Ex. : Café Néron")} className={inputClass} />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">{t("Adresse")}</label>
                <input data-testid="pe-address" value={form.address} onChange={set("address")} placeholder={t("Ex. : 123 rue Saint-Jean")} className={inputClass} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">{t("Ville")}</label>
                <input data-testid="pe-city" required value={form.city} onChange={set("city")} placeholder="Québec" className={inputClass} />
              </div>
            </div>

            <div>
              <label className="flex w-fit cursor-pointer items-center gap-2.5 text-sm font-semibold text-slate-700">
                <input
                  type="checkbox"
                  data-testid="pe-free-checkbox"
                  checked={form.is_free}
                  onChange={(e) => setForm((f) => ({ ...f, is_free: e.target.checked }))}
                  className="h-4 w-4 rounded border-slate-300 accent-[#2563EB]"
                />
                {t("Gratuit")}
              </label>
              {!form.is_free && (
                <div className="mt-3" data-testid="pe-price-field">
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">{t("Prix par personne ($)")}</label>
                  <input
                    data-testid="pe-price"
                    type="number"
                    min="1"
                    step="1"
                    required
                    value={form.price}
                    onChange={set("price")}
                    placeholder="15"
                    className={inputClass}
                  />
                </div>
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">{t("Lien de réservation (optionnel)")}</label>
                <input data-testid="pe-ticket-url" type="url" value={form.ticket_url} onChange={set("ticket_url")} placeholder="https://…" className={inputClass} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">{t("Téléphone (optionnel)")}</label>
                <input data-testid="pe-contact" type="tel" value={form.contact} onChange={set("contact")} placeholder="418-555-0000" className={inputClass} />
              </div>
            </div>

            {error && (
              <p data-testid="pe-error" className="rounded-2xl bg-red-50 px-4 py-2.5 text-sm text-red-600">{error}</p>
            )}
          </div>

          {/* PIED FIXE — deux boutons toujours visibles */}
          <div className="grid shrink-0 grid-cols-2 gap-2.5 border-t border-slate-100 bg-white px-6 py-4 sm:px-8">
            <button
              type="button"
              data-testid="pe-cancel"
              onClick={onClose}
              className="rounded-full border border-slate-200 py-3.5 text-sm font-semibold text-slate-600 transition hover:border-blue-300 hover:text-[#2563EB]"
            >
              {t("Annuler")}
            </button>
            <button
              type="submit"
              data-testid="pe-submit"
              disabled={busy || uploading}
              className="flex items-center justify-center gap-2 rounded-full bg-[#2563EB] py-3.5 text-sm font-semibold text-white transition hover:bg-[#1D4ED8] active:scale-[0.98] disabled:opacity-60"
            >
              <Send className="h-4 w-4" />
              {busy ? t("Publication…") : editing ? t("Enregistrer les modifications") : t("Publier l'événement")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
